<template>
  <div class="flex mb-4 group" :class="{ 'justify-end': message.from === 'user' }">
    <div class="flex items-start space-x-2 max-w-3xl" :class="{ 'flex-row-reverse space-x-reverse': message.from === 'user' }">
      
      <!-- Avatar ultra-minimalista -->
      <div class="flex-shrink-0">
        <div v-if="message.from === 'elias'" class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
          <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </div>
        <div v-else-if="message.from === 'user'" class="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center">
          <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <div v-else-if="message.from === 'system'" class="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
          <svg class="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>
      
      <!-- Mensaje ultra-simple -->
      <div class="flex-1 min-w-0">
        <div :class="bubbleClass">
          <div class="message-content" v-html="formattedText"></div>
          
          <!-- Renderizar instrucciones si existen -->
          <div v-if="message.payload && message.payload.instrucciones" class="mt-4 pl-4 border-l-2 border-blue-200">
            <ul class="list-disc list-inside space-y-2">
              <li v-for="(instruccion, index) in message.payload.instrucciones" :key="index" v-html="formatInstruction(instruccion)"></li>
            </ul>
          </div>
        </div>
        
        <!-- Renderizar botones de feedback si es necesario -->
        <FeedbackButtons v-if="message.payload && message.payload.espera_feedback" />
        
        <!-- Timestamp y acciones mejorado con estados de mensaje -->
        <div class="flex items-center justify-between mt-1 opacity-0 group-hover:opacity-50 transition-opacity duration-200">
          <!-- Lado izquierdo: timestamp -->
          <span class="text-xs text-gray-400">{{ formattedTimestamp }}</span>
          
          <!-- Lado derecho: estados de mensaje y acciones -->
          <div class="flex items-center space-x-2">
            <!-- Estados de mensaje para mensajes del usuario -->
            <MessageStatus 
              v-if="message.from === 'user'"
              :status="message.status || 'delivered'"
              :timestamp="message.timestamp"
              :show-timestamp="false"
              :is-user-message="true"
            />
            
            <!-- Botón de copiar -->
            <button 
              @click="copyMessage" 
              class="p-1 rounded hover:bg-gray-100 transition-colors duration-150"
              title="Copiar"
            >
              <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import MessageStatus from './MessageStatus.vue';
import FeedbackButtons from './FeedbackButtons.vue'; // Importar el nuevo componente

// Props simplificadas
const props = defineProps({
  message: {
    type: Object,
    required: true,
    validator: (message) => {
      return message && message.from && message.text;
    }
  },
  showTimestamp: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['regenerate']);

// Reactive state
const showActions = ref(false);

// Estilo de burbuja ultra-minimalista
const bubbleClass = computed(() => {
  const baseClasses = 'px-3 py-2 rounded-lg text-sm';
  
  switch (props.message.from) {
    case 'elias':
      return `${baseClasses} bg-gray-50 text-gray-900`;
    case 'user':
      return `${baseClasses} bg-blue-500 text-white`;
    case 'system':
      return `${baseClasses} bg-amber-50 text-amber-800`;
    default:
      return `${baseClasses} bg-gray-50 text-gray-900`;
  }
});

// Formateo de texto optimizado
const formattedText = computed(() => {
  if (!props.message.text) return '';
  
  let text = props.message.text;
  
  // Formateo básico mejorado
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong class="font-medium">$1</strong>');
  text = text.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');
  text = text.replace(/`(.*?)`/g, '<code class="bg-gray-100 px-2 py-0.5 rounded text-sm font-mono">$1</code>');
  text = text.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" class="text-blue-600 underline hover:text-blue-700 transition-colors">$1</a>');
  text = text.replace(/\n/g, '<br>');
  
  return text;
});

const formatInstruction = (instruction) => {
  let text = instruction;
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong class="font-medium">$1</strong>');
  text = text.replace(/`(.*?)`/g, '<code class="bg-gray-100 px-2 py-0.5 rounded text-sm font-mono">$1</code>');
  return text;
};

// Format timestamp
const formattedTimestamp = computed(() => {
  if (!props.message.timestamp) return '';
  const date = new Date(props.message.timestamp);
  return date.toLocaleTimeString('es-ES', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false
  });
});

// Actions
function copyMessage() {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(props.message.text).then(() => {
      // Show temporary success feedback
      showCopyFeedback();
    }).catch(err => {
      console.error('Error copying message:', err);
      fallbackCopy();
    });
  } else {
    fallbackCopy();
  }
}

function fallbackCopy() {
  // Fallback for older browsers
  const textArea = document.createElement('textarea');
  textArea.value = props.message.text;
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand('copy');
    showCopyFeedback();
  } catch (err) {
    console.error('Fallback copy failed:', err);
  }
  document.body.removeChild(textArea);
}

function showCopyFeedback() {
  // Create temporary toast notification
  const toast = document.createElement('div');
  toast.textContent = 'Mensaje copiado';
  toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300';
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.classList.add('opacity-0', 'transform', 'translate-y-2');
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 300);
  }, 2000);
}

function regenerateResponse() {
  emit('regenerate', props.message);
}

function handleImageError(event) {
  // Fallback to a gradient avatar if image fails to load
  event.target.style.display = 'none';
  const parent = event.target.parentElement;
  parent.innerHTML = `
    <div class="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
      <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
    </div>
  `;
}
</script>

<style scoped>
.order-last {
  order: 1;
}
</style>
